/* @flow */
import { VALIDATION_PATTERNS } from "nexia_mobile/framework/validatable";
import type { FormField } from "nexia_mobile/api/types";
import { getLanguageSet, template } from "nx-components/Locales";

export type FieldValidationResult =
  | {
      hasErrors: false,
      nameOfFirstFieldWithError: null,
      errors: {||},
    }
  | {
      hasErrors: true,
      nameOfFirstFieldWithError: string,
      errors: { [fieldName: string]: Array<string> },
    };

export function getValueForField(
  field: FormField,
  fieldValuesObject: { [fieldName: string]: any }
) {
  let value;
  if ({}.hasOwnProperty.call(fieldValuesObject, field.name)) {
    value = fieldValuesObject[field.name];
  } else {
    // If the value is not specified in the fieldValuesObject, check the
    // field definition itself. A value will be present there when editing
    // an existing resource, for example.
    // eslint-disable-next-line no-lonely-if
    if (field.type === "color") {
      value = {
        warm: field.warm || 0,
        cool: field.cool || 0,
        red: field.red || 0,
        green: field.green || 0,
        blue: field.blue || 0,
      };
    } else if (typeof field.value !== "undefined") {
      value = field.value;
    }
  }

  if (field.type === "boolean" && value == null) {
    value = false;
  }

  return value;
}

export function isOrphanedField(
  field: FormField,
  fields: Array<FormField>,
  fieldValuesObject: { [fieldName: string]: any }
) {
  if (field.dependency == null && field.hideIf == null) {
    return false;
  }

  if (field.dependency) {
    return (
      fields.findIndex(
        (otherField) =>
          getValueForField(otherField, fieldValuesObject) === field.dependency
      ) === -1
    );
  }

  if (field.hideIf) {
    const { hideIf } = field;
    switch (hideIf.operator) {
      case "one_of": {
        const parentField = fields.find(
          (otherField) => otherField.name === hideIf.fieldName
        );
        if (parentField == null) {
          return false;
        }
        return hideIf.argument.includes(
          getValueForField(parentField, fieldValuesObject)
        );
      }
    }
  }
  return false;
}

export default function validateFields(
  fields: Array<FormField>,
  fieldValuesObject: { [fieldName: string]: any },
  options: {
    validateUnsetFields: boolean,
  }
): FieldValidationResult {
  const localeSet = getLanguageSet();
  const language = localeSet.nexia_mobile.utils.validateFields;
  let hasErrors = false;
  let nameOfFirstFieldWithError = null;
  const errors = {};

  fields.forEach((field) => {
    if (
      options.validateUnsetFields === false &&
      fieldValuesObject[field.name] == null
    ) {
      return;
    }

    const addError = (message) => {
      hasErrors = true;
      if (nameOfFirstFieldWithError == null) {
        nameOfFirstFieldWithError = field.name;
      }
      if (errors[field.name] == null) {
        errors[field.name] = [];
      }
      errors[field.name].push(message);
    };

    const value = getValueForField(field, fieldValuesObject);
    const label = field.label || field.name;

    // For string templates only.
    const fieldLength = field.length || 0;
    const fieldMinLength = field.minLength || 0;
    const fieldMaxLength = field.maxLength || 0;
    const fieldMin = field.min || 0;
    const fieldMax = field.max || 0;

    const LABEL_REQUIRED = template(language.labelRequiredTemplate, { label });
    const LABEL_DOES_NOT_MATCH = template(language.labelDoesNotMatchTemplate, {
      label,
    });
    const LABEL_INVALID = template(language.labelInvalidTemplate, { label });
    const LABEL_EXACT_LENGTH_CHARS = template(
      language.labelExactLengthCharsTemplate,
      { label, fieldLength }
    );
    const LABEL_MIN_LENGTH_CHARS = template(
      language.labelMinLengthCharsTemplate,
      { label, fieldMinLength }
    );
    const LABEL_MAX_LENGTH_CHARS = template(
      language.labelMaxLengthCharsTemplate,
      { label, fieldMaxLength }
    );
    const LABEL_MIN_NUMBER = template(language.labelMinNumberTemplate, {
      label,
      fieldMin,
    });
    const LABEL_MAX_NUMBER = template(language.labelMaxNumberTemplate, {
      label,
      fieldMax,
    });
    const LABEL_EARLY = template(language.labelEarlyTemplate, { label });
    const LABEL_LATE = template(language.labelLateTemplate, { label });

    // Handle required fields
    if (
      field.required &&
      (value == null || // null or undefined
      value.toString().trim() === "" || // empty string or whitespace only
      (field.type === "select" && value === "_default") || // a select item placeholder
        (field.type === "boolean" && value !== true)) // for boolean fields, required means "must be true"
    ) {
      if (isOrphanedField(field, fields, fieldValuesObject)) {
        // It's okay for this field to be omitted even though it is listed
        // as required, because its parent field's value is not set.
      } else {
        addError(field.requiredMessage || field.message || LABEL_REQUIRED);
      }
    }

    // Equality with another field
    if (field.equalTo != null) {
      let equalToField = fields.find(
        (otherField) => otherField.name === field.equalTo
      );
      if (
        equalToField != null &&
        value !== getValueForField(equalToField, fieldValuesObject)
      ) {
        addError(LABEL_DOES_NOT_MATCH);
      }
    }

    let stringValue = value != null ? value.toString() : "";
    if (stringValue === "" && !field.required) {
      // If the field is not required, it is allowed to be empty even if that fails other validations
      return;
    }

    // Handle validation patterns
    let pattern = field.pattern && new RegExp(field.pattern);
    if (field.patternType) {
      pattern = VALIDATION_PATTERNS[field.patternType];
    }

    if (pattern && !stringValue.match(pattern)) {
      addError(field.message || LABEL_INVALID);
    }

    // Handle string length
    if (
      field.type === "text" ||
      field.type === "password" ||
      field.type === "email"
    ) {
      if (typeof field.length === "number") {
        if (typeof value !== "string" || value.length !== field.length) {
          addError(LABEL_EXACT_LENGTH_CHARS);
        }
      }

      if (typeof field.minLength === "number") {
        if (typeof value !== "string" || value.length < field.minLength) {
          addError(LABEL_MIN_LENGTH_CHARS);
        }
      }

      if (typeof field.maxLength === "number") {
        if (typeof value === "string" && value.length > field.maxLength) {
          addError(LABEL_MAX_LENGTH_CHARS);
        }
      }
    }

    // Handle number min/max
    if (
      typeof value === "number" &&
      (field.type === "number" || field.type === "range")
    ) {
      if (typeof field.min === "number" && typeof value === "number") {
        if (value < field.min) {
          addError(LABEL_MIN_NUMBER);
        }
      }

      if (typeof field.max === "number") {
        if (value > field.max) {
          addError(LABEL_MAX_NUMBER);
        }
      }
    }

    // Handle date min/max
    if (field.type === "date") {
      if (field.min != null) {
        // $FlowFixMe: field.min is not refined from `string | void` to `string` here for some reason
        if (Date.parse(value) < Date.parse(field.min)) {
          addError(LABEL_EARLY);
        }
      }

      if (field.max != null) {
        // $FlowFixMe: field.max is not refined from `string | void` to `string` here for some reason
        if (Date.parse(value) > Date.parse(field.max)) {
          addError(LABEL_LATE);
        }
      }
    }
  });

  // $FlowIgnore: flow can't tell that errors is guaranteed to be empty when hasErrors is false
  return {
    hasErrors,
    errors,
    nameOfFirstFieldWithError,
  };
}



// WEBPACK FOOTER //
// ./src/validateFields.js